import { Injectable } from '@angular/core';
import {
  PaymentLinkV2,
  EstablishmentV2,
  SettingV2,
  PixV2,
  DreampayPaymentResponseV2,
  CardInformationV2,
  PixTransactionRequestV2,
  BinLookupRequestV2,
} from 'api-client';
import { Observable, of, throwError, delay } from 'rxjs';

/**
 * Mock API Service for Payment Testing
 *
 * Provides realistic mock data for all payment-related API endpoints.
 * Used for E2E testing and development without a live backend.
 */
@Injectable({
  providedIn: 'root',
})
export class MockPaymentApiService {
  // Configuration options
  private simulateDelay = true;
  private delayMs = 500;
  private shouldFail = false;

  // Mock data storage
  private mockPaymentLinks = new Map<string, PaymentLinkV2>();
  private mockEstablishments = new Map<number, EstablishmentV2>();
  private mockPix = new Map<string, PixV2>();
  private mockSettings: SettingV2;

  constructor() {
    this.initializeMockData();
    this.mockSettings = this.createMockSettings();
  }

  /**
   * Configure mock service behavior
   */
  configure(options: { simulateDelay?: boolean; delayMs?: number; shouldFail?: boolean }): void {
    if (options.simulateDelay !== undefined) {
      this.simulateDelay = options.simulateDelay;
    }
    if (options.delayMs !== undefined) {
      this.delayMs = options.delayMs;
    }
    if (options.shouldFail !== undefined) {
      this.shouldFail = options.shouldFail;
    }
  }

  /**
   * Initialize mock data for testing
   */
  private initializeMockData(): void {
    // Mock Payment Link - EUR
    const paymentLink1: PaymentLinkV2 = {
      id: 'test-payment-link-1',
      establishmentId: 1,
      establishmentName: 'Test Restaurant Lisboa',
      value: 100.0,
      currencyCode: 'EUR',
      customerEmail: 'customer@test.com',
      orderNumber: 'ORDER-001',
      observations: 'Test order for pizza',
      paid: false,
      paidToEstablishment: false,
      customerName: 'João Silva',
      date: new Date().toISOString(),
      link: 'https://pay.dreampay.com.br/test-payment-link-1',
      expired: false,
      canceled: false,
      callbackUrl: 'https://merchant.com/callback',
      customerPhoneNumber: '+351912345678',
      qrcodeUrl: undefined,
      returnUrl: 'https://merchant.com/return',
      formHeaderHtml: undefined,
      formFooterHtml: undefined,
      linkType: 1,
      establishmentSupportsFinancingCosts: false,
      pdfUrl: undefined,
    };

    // Mock Payment Link - USD
    const paymentLink2: PaymentLinkV2 = {
      id: 'test-payment-link-2',
      establishmentId: 1,
      establishmentName: 'Test Restaurant Lisboa',
      value: 50.0,
      currencyCode: 'USD',
      customerEmail: 'customer2@test.com',
      orderNumber: 'ORDER-002',
      observations: 'Test order for coffee',
      paid: false,
      paidToEstablishment: false,
      customerName: 'Maria Santos',
      date: new Date().toISOString(),
      link: 'https://pay.dreampay.com.br/test-payment-link-2',
      expired: false,
      canceled: false,
      callbackUrl: undefined,
      customerPhoneNumber: '+351923456789',
      qrcodeUrl: undefined,
      returnUrl: undefined,
      formHeaderHtml: undefined,
      formFooterHtml: undefined,
      linkType: 1,
      establishmentSupportsFinancingCosts: true,
      pdfUrl: undefined,
    };

    // Mock Expired Payment Link
    const paymentLink3: PaymentLinkV2 = {
      id: 'test-payment-link-expired',
      establishmentId: 1,
      establishmentName: 'Test Restaurant Lisboa',
      value: 75.0,
      currencyCode: 'EUR',
      customerEmail: 'expired@test.com',
      orderNumber: 'ORDER-003',
      observations: undefined,
      paid: false,
      paidToEstablishment: false,
      customerName: undefined,
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      link: 'https://pay.dreampay.com.br/test-payment-link-expired',
      expired: true,
      canceled: false,
      callbackUrl: undefined,
      customerPhoneNumber: undefined,
      qrcodeUrl: undefined,
      returnUrl: undefined,
      formHeaderHtml: undefined,
      formFooterHtml: undefined,
      linkType: 1,
      establishmentSupportsFinancingCosts: false,
      pdfUrl: undefined,
    };

    // Mock Paid Payment Link
    const paymentLink4: PaymentLinkV2 = {
      id: 'test-payment-link-paid',
      establishmentId: 1,
      establishmentName: 'Test Restaurant Lisboa',
      value: 200.0,
      currencyCode: 'EUR',
      customerEmail: 'paid@test.com',
      orderNumber: 'ORDER-004',
      observations: 'Already paid order',
      paid: true,
      paidToEstablishment: false,
      customerName: 'Pedro Costa',
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      link: 'https://pay.dreampay.com.br/test-payment-link-paid',
      expired: false,
      canceled: false,
      callbackUrl: undefined,
      customerPhoneNumber: '+351934567890',
      qrcodeUrl: undefined,
      returnUrl: undefined,
      formHeaderHtml: undefined,
      formFooterHtml: undefined,
      linkType: 1,
      establishmentSupportsFinancingCosts: false,
      pdfUrl: 'https://dreampay.com.br/receipts/test.pdf',
    };

    this.mockPaymentLinks.set('test-payment-link-1', paymentLink1);
    this.mockPaymentLinks.set('test-payment-link-2', paymentLink2);
    this.mockPaymentLinks.set('test-payment-link-expired', paymentLink3);
    this.mockPaymentLinks.set('test-payment-link-paid', paymentLink4);

    // Mock Establishment
    const establishment1: EstablishmentV2 = {
      id: 1,
      establismentName: 'Test Restaurant Lisboa, Lda',
      commercialName: 'Test Restaurant Lisboa',
      softDescriptor: 'TEST_REST_LIS',
      address: 'Rua Augusta, 123',
      state: 'Lisboa',
      zipCode: '1100-048',
      city: 'Lisboa',
      countryId: 1,
      vatNumber: 'PT123456789',
      iban: 'PT50000123456789012345678',
      swiftCode: 'TESTPTPL',
      phoneNumber: '+351211234567',
      emailAddress: 'info@testrestaurant.pt',
      websiteUrl: 'https://testrestaurant.pt',
      instagramUrl: 'https://instagram.com/testrestaurant',
      logo: 'https://dreampay.com.br/logos/test-restaurant.png',
      comission: 0.035,
      deleted: false,
      apiKey: 'test-api-key-12345',
      signUpDate: '2024-01-15T10:00:00',
      warrantyValueEur: 5000.0,
      contractUuid: 'contract-uuid-12345',
      signedContractUrl: 'https://dreampay.com.br/contracts/test.pdf',
      active: true,
      showInApp: true,
      currencyCode: 'EUR',
      averageSaleValue: 75.0,
      bankName: 'Banco Test Portugal',
      bankAddress: 'Av. da Liberdade, 456, Lisboa',
    };

    this.mockEstablishments.set(1, establishment1);
  }

  /**
   * Create mock settings with realistic exchange rates and fees
   */
  private createMockSettings(): SettingV2 {
    return {
      date: Date.now(),
      oficialExchangeRate: 5.85, // EUR to BRL
      spread: 0.025,
      iof: 0.0138,
      operacionalMargin: 0.03,
      pixProcessmentFee: 0.0099,
      cardProcessmentFee: 0.0304,
      debitCardProcessmentFee: 0.0079,
      antecipationFee: 0.0115,
      financingFee: 0.005,
      twoMonthsFee: 0.0229,
      threeMonthsFee: 0.0229,
      fourMonthsFee: 0.0229,
      fiveMonthsFee: 0.0229,
      sixMonthsFee: 0.0229,
      sevenMonthsFee: 0.0224,
      eightMonthsFee: 0.0224,
      nineMonthsFee: 0.0224,
      tenMonthsFee: 0.0224,
      elevenMonthsFee: 0.0224,
      twelveMonthsFee: 0.0224,
    };
  }

  /**
   * Apply delay if configured
   */
  private applyDelay<T>(observable: Observable<T>): Observable<T> {
    if (this.simulateDelay) {
      return observable.pipe(delay(this.delayMs));
    }
    return observable;
  }

  /**
   * Mock: Get payment link by ID
   */
  getPaymentLink(id: string): Observable<PaymentLinkV2> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('Failed to fetch payment link')));
    }

    const paymentLink = this.mockPaymentLinks.get(id);
    if (!paymentLink) {
      return this.applyDelay(throwError(() => new Error('Payment link not found')));
    }

    return this.applyDelay(of(paymentLink));
  }

  /**
   * Mock: Get establishment by ID
   */
  getEstablishment(id: number): Observable<EstablishmentV2> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('Failed to fetch establishment')));
    }

    const establishment = this.mockEstablishments.get(id);
    if (!establishment) {
      return this.applyDelay(throwError(() => new Error('Establishment not found')));
    }

    return this.applyDelay(of(establishment));
  }

  /**
   * Mock: Get settings (exchange rates and fees)
   */
  getSettings(): Observable<SettingV2> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('Failed to fetch settings')));
    }

    return this.applyDelay(of(this.mockSettings));
  }

  /**
   * Mock: Create PIX payment
   */
  createPixPayment(request: PixTransactionRequestV2): Observable<DreampayPaymentResponseV2> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('Failed to create PIX payment')));
    }

    const tid = `TID-PIX-${Date.now()}`;
    const qrCodeData = `00020126580014br.gov.bcb.pix0136${tid}520400005303986540${(request.amount! / 100).toFixed(2)}5802BR5925Test Restaurant Lisboa6009SAO PAULO62070503***6304`;

    const response: DreampayPaymentResponseV2 = {
      returnCode: '0',
      returnMessage: 'Transaction successful',
      paymentId: `PAY-${Date.now()}`,
      tid: tid,
      authorizationCode: undefined,
      proofSale: undefined,
      paymentAccountReference: request.paymentLinkId || undefined,
      receivedDate: new Date().toISOString(),
    };

    // Store PIX data
    const pix: PixV2 = {
      tid: tid,
      qrcodeResponseImage: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==`,
      qrcodeResponseData: qrCodeData,
      qrcodeResponseDateTimeExpiration: request.qrCode?.dateTimeExpiration || undefined,
      reference: request.reference || undefined,
      returnCode: '0',
      returnMessage: 'PIX created successfully',
      status: 'Pending',
      purchaseCurrency: request.purchaseCurrency || undefined,
      paymentLinkId: request.paymentLinkId || undefined,
    };

    if (request.paymentLinkId) {
      this.mockPix.set(request.paymentLinkId, pix);
    }

    return this.applyDelay(of(response));
  }

  /**
   * Mock: Get PIX by payment link ID
   */
  getPixByPaymentLink(paymentLinkId: string): Observable<PixV2> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('Failed to fetch PIX')));
    }

    const pix = this.mockPix.get(paymentLinkId);
    if (!pix) {
      return this.applyDelay(throwError(() => new Error('PIX not found')));
    }

    return this.applyDelay(of(pix));
  }

  /**
   * Mock: Process credit/debit card payment
   */
  processCardPayment(
    paymentRequest: Record<string, unknown>,
  ): Observable<DreampayPaymentResponseV2> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('Payment declined')));
    }

    const response: DreampayPaymentResponseV2 = {
      returnCode: '0',
      returnMessage: 'Transaction approved',
      paymentId: `PAY-CARD-${Date.now()}`,
      tid: `TID-CARD-${Date.now()}`,
      authorizationCode: `AUTH-${Math.random().toString(36).substring(7).toUpperCase()}`,
      proofSale: `NSU-${Math.random().toString(36).substring(7).toUpperCase()}`,
      paymentAccountReference: (paymentRequest['paymentLinkId'] as string) || undefined,
      receivedDate: new Date().toISOString(),
    };

    return this.applyDelay(of(response));
  }

  /**
   * Mock: BIN lookup for card validation
   */
  lookupBin(request: BinLookupRequestV2): Observable<CardInformationV2> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('BIN lookup failed')));
    }

    const cardNumber = request.cardNumber || '';
    const bin = cardNumber.substring(0, 8);

    // Mock different card types based on BIN
    let cardType = 'VISA';
    let cardBrand = 'Visa';

    if (bin.startsWith('5')) {
      cardType = 'MASTERCARD';
      cardBrand = 'Mastercard';
    } else if (bin.startsWith('4')) {
      cardType = 'VISA';
      cardBrand = 'Visa';
    } else if (bin.startsWith('3')) {
      cardType = 'AMEX';
      cardBrand = 'American Express';
    } else if (bin.startsWith('6')) {
      cardType = 'ELO';
      cardBrand = 'Elo';
    }

    const cardInfo: CardInformationV2 = {
      bin: bin,
      bank: 'Test Bank',
      card: cardBrand,
      type: cardType,
      level: 'CREDIT',
      country: 'Brazil',
      countrycode: 'BR',
      website: 'https://testbank.com',
      phone: '+55 11 1234-5678',
      valid: 'true',
    };

    return this.applyDelay(of(cardInfo));
  }

  /**
   * Mock: Validate CPF
   */
  validateCpf(cpf: string): Observable<{ valid: boolean; message?: string }> {
    if (this.shouldFail) {
      return this.applyDelay(throwError(() => new Error('CPF validation failed')));
    }

    // Simple mock validation - accepts any 11-digit CPF
    const valid = /^\d{11}$/.test(cpf);

    return this.applyDelay(
      of({
        valid: valid,
        message: valid ? 'CPF is valid' : 'CPF is invalid',
      }),
    );
  }

  /**
   * Get all mock payment links (for testing)
   */
  getAllPaymentLinks(): PaymentLinkV2[] {
    return Array.from(this.mockPaymentLinks.values());
  }

  /**
   * Update payment link (for testing)
   */
  updatePaymentLink(id: string, updates: Partial<PaymentLinkV2>): void {
    const paymentLink = this.mockPaymentLinks.get(id);
    if (paymentLink) {
      this.mockPaymentLinks.set(id, { ...paymentLink, ...updates });
    }
  }

  /**
   * Reset all mock data (for testing)
   */
  reset(): void {
    this.mockPaymentLinks.clear();
    this.mockEstablishments.clear();
    this.mockPix.clear();
    this.initializeMockData();
    this.mockSettings = this.createMockSettings();
    this.shouldFail = false;
    this.simulateDelay = true;
    this.delayMs = 500;
  }
}
